import {
  changeLocale,
  isSupportedLocale,
  SAVED_OR_DEFAULT_LOCALE,
  setLocaleInPath,
  SUPPORTED_LOCALES,
} from "@/i18n";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useParams } from "react-router";

export default function LocaleWrapper({ children }: { children: ReactNode }) {
  const { lng } = useParams<{ lng: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();

  // This is the ONLY place changeLocale is called.
  // All locale changes flow through URL → LocaleWrapper → changeLocale.
  // Must be called BEFORE any conditional returns (Rules of Hooks).
  useEffect(() => {
    if (!lng || !isSupportedLocale(lng)) return;

    // The server already emits the correct `lang`, but the Vite fallback and
    // client-side navigation must stay correct as well. Do not tie these
    // attributes to `i18n.language !== lng`: on a direct localized URL i18n
    // can already have the requested language while the generic HTML shell
    // still carries its default language.
    document.documentElement.lang = lng;
    document.documentElement.dir = SUPPORTED_LOCALES[lng].dir ?? "ltr";

    if (i18n.language !== lng) void changeLocale(lng);
  }, [lng, i18n.language]);

  // Redirect uppercase/mixed-case locale codes to lowercase
  if (lng && lng !== lng.toLowerCase()) {
    const redirectPath = setLocaleInPath(
      lng.toLowerCase(),
      location.pathname,
      location.search,
      location.hash,
    );
    return <Navigate to={redirectPath} replace />;
  }

  // If locale is invalid, redirect with saved/default locale
  if (!lng || !isSupportedLocale(lng)) {
    const redirectPath = setLocaleInPath(
      SAVED_OR_DEFAULT_LOCALE,
      location.pathname,
      location.search,
      location.hash,
    );
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}
